<script setup>
  import {computed, inject, onUnmounted, ref} from "vue";

  import ComponentAiRating from "@components/AiRating.vue";
  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDoNotShowAgain from "@components/Checkboxes/DoNotShowAgain.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIconAiSparkle from "@components/Icons/IconAiSparkle.vue";
  import PagesRecordResultListClaimItem from "@pages/Records/Components/Result/ResultList/ListClaimItem.vue";
  import {
    MedAlert,
    MedButton,
    MedCheckbox,
    MedFormField,
    MedInfoButton,
    MedLabel,
    MedSpinner,
    MedTag,
  } from "@pharma4u/medicheck-components";
  import {extractAiErrorMessage} from "@utils/Helpers/AiErrorMessage.js";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentAiPrioritizationDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    checks: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(["generated"]);

  const privacy = inject("$privacy");
  const resultConfig = inject("resultConfig");
  const addToastMessage = inject("addToastMessage");

  const refDialog = ref(null);

  // 'hint' | 'progress' | 'result' | 'error'
  const state = ref("hint");
  const showProgressLines = ref(true);
  const isLoading = ref(false);
  const error = ref(null);

  const sectionIds = ref([]);
  const reasoning = ref(null);
  const selected = ref({});

  const rating = ref(null);
  const ratingReason = ref(null);

  const progressLines = [
    "⏱️ Analysiere Fall-Kontext und extrahiere Informationen aus MediCheck Ergebnissen...",
    "🔒 System-Check: Fokus ausschließlich auf MediCheck-Ergebnissen...",
    "📊 Analysiere klinische Relevanz aller Ergebnisse...",
    "🎯 Filterprozess aktiv: Identifiziere Top-3-Prioritäten für diesen Patientenfall...",
    "✨ Bereite unverbindliche Vorschlagsliste inklusive Begründung vor...",
  ];
  const visibleProgressLines = ref([]);
  let progressTimer = null;
  let revealTimer = null;

  const upOptions = [
    {value: 0, label: "Klinisch relevante Ergebnisse wurden priorisiert"},
    {value: 1, label: "Die Begründung war hilfreich"},
    {value: 2, label: "Die KI-Funktion spart Zeit"},
    {value: "custom", label: "Freitext"},
  ];
  const downOptions = [
    {value: 0, label: "Klinisch nicht relevante Ergebnisse wurden priorisiert."},
    {value: 1, label: "Die Begründung war fehlerhaft/unklar"},
    {value: 2, label: "Die KI-Funktion spart keine Zeit"},
    {value: "custom", label: "Freitext"},
  ];

  const claimsBySection = computed(() => {
    const map = {};
    for (const check of props.checks ?? []) {
      for (const claim of check.claims ?? []) {
        map[claim.section] = {claim, check};
      }
    }
    return map;
  });

  const items = computed(() =>
    sectionIds.value.map((section) => ({section, ...claimsBySection.value[section]})).filter((item) => item.claim),
  );

  const resultsLabel = computed(() => {
    const count = items.value.length;
    if (count === 1) return "dieses Ergebnis";
    const number = {2: "zwei", 3: "drei"}[count] ?? count;
    return `diese ${number} Ergebnisse`;
  });

  // group claims by check to render the category heading above the rows, as on the result page
  const groupedItems = computed(() => {
    const groups = new Map();
    for (const item of items.value) {
      if (!groups.has(item.check.id)) {
        groups.set(item.check.id, {check: item.check, items: []});
      }
      groups.get(item.check.id).items.push(item);
    }
    return [...groups.values()];
  });

  const startProgressLines = () => {
    visibleProgressLines.value = [progressLines[0]];
    let index = 1;
    stopProgressLines();
    progressTimer = setInterval(() => {
      if (index >= progressLines.length) {
        clearInterval(progressTimer);
        progressTimer = null;
        return;
      }
      visibleProgressLines.value.push(progressLines[index]);
      index += 1;
    }, 2000);
  };

  const stopProgressLines = () => {
    if (progressTimer) {
      clearInterval(progressTimer);
      progressTimer = null;
    }
    if (revealTimer) {
      clearTimeout(revealTimer);
      revealTimer = null;
    }
  };

  onUnmounted(() => {
    stopProgressLines();
    request?.cancel();
  });

  const reveal = () => {
    stopProgressLines();
    state.value = "result";
    emit("generated");
  };

  const resetResult = () => {
    sectionIds.value = [];
    reasoning.value = null;
    selected.value = {};
    rating.value = null;
    ratingReason.value = null;
  };

  // hold the result until the progress lines have played out, so it never feels instant
  const MIN_GENERATION_MS = 10_000;

  // remember how the last generation was triggered so retry repeats it
  const lastIsReopen = ref(false);

  // kept so we can cancel the request while it's still loading
  let request = null;

  const generate = (isReopen = false) => {
    request?.cancel();

    lastIsReopen.value = isReopen;
    isLoading.value = true;
    error.value = null;
    resetResult();
    const startedAt = Date.now();
    if (showProgressLines.value) {
      startProgressLines();
    } else {
      visibleProgressLines.value = [];
    }

    request = useAuthHttp();
    request.get(
      route("api.record.relevant-claims", {
        patient: props.record.patient_id,
        record: props.record.id,
      }),
      {
        onSuccess: (data) => {
          sectionIds.value = data.section_ids ?? [];
          reasoning.value = data.reasoning ?? null;

          // when re-opening, reflect the sections actually applied in the result filter;
          // a fresh generation pre-selects all suggestions
          const applied = resultConfig.value.aiPrioritizedClaims ?? [];
          selected.value = Object.fromEntries(
            sectionIds.value.map((id) => [id, isReopen ? applied.includes(id) : true]),
          );

          // restore the rating previously stored on the record
          rating.value = data.rating ?? null;
          ratingReason.value = data.rating_reason ?? null;

          // let the progress lines finish before revealing the result
          const remaining = showProgressLines.value ? MIN_GENERATION_MS - (Date.now() - startedAt) : 0;
          if (remaining > 0) {
            revealTimer = setTimeout(reveal, remaining);
          } else {
            reveal();
          }
        },

        onError: (errors) => (error.value = Object.values(errors).join("\n")),
        onNetworkError: (error_) => (error.value = error_.message),

        onHttpException: (response) =>
          (error.value = extractAiErrorMessage(
            response.data,
            "Es ist ein Fehler bei der Priorisierung aufgetreten. Bitte versuchen Sie es später erneut.",
          )),

        onFinish: () => {
          isLoading.value = false;
          // on error, stop the progress lines and switch to the error state
          if (error.value) {
            stopProgressLines();
            state.value = "error";
          }
        },
        onCancel: () => {
          isLoading.value = false;
          stopProgressLines();
        },
      },
    );
  };

  const updateResponse = (action = null, selectedSectionIds = null) => {
    return useAuthHttp({
      action,
      rating: rating.value,
      ratingReason: ratingReason.value,
      selectedSectionIds,
    }).post(
      route("api.record.relevant-claims.update", {
        patient: props.record.patient_id,
        record: props.record.id,
      }),
    );
  };

  const open = (initialState = "hint") => {
    error.value = null;

    // re-opening a cached result: just spinner, no progress lines
    if (initialState === "result") {
      showProgressLines.value = false;
      state.value = "progress";
      refDialog.value.open();
      generate(true);
      return;
    }

    // fresh generation: full hint → progress → result flow
    showProgressLines.value = true;

    const warning = privacy.getConfiguration("aiBasedPrioritizationWarning");
    if (warning?.amount === false) {
      state.value = "progress";
      refDialog.value.open();
      generate();
      return;
    }

    state.value = "hint";
    refDialog.value.open();
  };

  const startFromHint = () => {
    showProgressLines.value = true;
    state.value = "progress";
    generate();
  };

  const apply = () => {
    const checkedIds = sectionIds.value.filter((id) => selected.value[id]);

    resultConfig.value.aiPrioritizedClaims = checkedIds;

    if (checkedIds.length === 0) {
      updateResponse("abort");
    } else {
      updateResponse("accept", checkedIds);
      addToastMessage("Priorisierung wurde angewendet.", "success", 5000);
    }

    refDialog.value.close();
  };

  const retry = () => {
    state.value = "progress";
    generate(lastIsReopen.value);
  };

  const cancelProgress = () => {
    request?.cancel();
    stopProgressLines();
    refDialog.value.close();
  };

  const cancelResult = () => {
    if (!lastIsReopen.value) {
      updateResponse("abort");
    } else {
      updateResponse(); // only update rating
    }

    refDialog.value.close();
  };

  defineExpose({open});
</script>

<template>
  <ComponentDialog ref="refDialog" dialog-width="large" :test-id="getTestId('ai-prioritization-dialog')">
    <template #content>
      <template v-if="state === 'hint'">
        <h1 class="mb-3 flex items-center gap-2 text-2xl">
          <ComponentIconAiSparkle class="h-6 w-auto" />
          KI-Priorisierung
        </h1>

        <p class="mb-4">
          Diese MediCheck-KI Funktion ermöglicht Ihnen,
          <strong>relevante Ergebnisse</strong>
          zu filtern.
        </p>

        <MedAlert variant="info" icon="info">
          Ihre Daten sind jederzeit geschützt. Die MediCheck-KI generiert keine neuen Fakten, sondern filtert die
          MediCheck-Ergebnisse nach klinischer Relevanz.
          <br />

          Bitte beachten Sie, dass jede KI Fehler machen oder unvollständige Angaben erzeugen kann. Überprüfen Sie daher
          alle Fakten und wichtigen Informationen sorgfältig.
        </MedAlert>

        <ComponentDoNotShowAgain class="mt-4" settings-name="aiBasedPrioritizationWarning" />
      </template>

      <template v-else-if="state === 'progress'">
        <h1 class="mb-3 flex items-center gap-2 text-2xl">
          <ComponentIconAiSparkle class="h-6 w-auto" />
          KI-Priorisierung
        </h1>

        <template v-if="showProgressLines">
          <p class="mb-4">
            Einen kurzen Augenblick noch. Die MediCheck-KI bewertet für Sie die Relevanz aller Ergebnisse.
          </p>

          <ul class="space-y-2">
            <li v-for="(line, idx) in visibleProgressLines" :key="idx" class="text-sm text-gray-700">
              {{ line }}
            </li>
          </ul>
        </template>

        <template v-else>
          <div class="my-10 flex flex-col items-center justify-center gap-5">
            <MedSpinner size="3xl" :data-test="getTestId('ai-prioritization-dialog-spinner')" />
          </div>
        </template>
      </template>

      <template v-else-if="state === 'error'">
        <h1 class="mb-3 flex items-center gap-2 text-2xl">
          <ComponentIconAiSparkle class="h-6 w-auto" />
          KI-Priorisierung
        </h1>

        <ComponentAlert type="error" class="mt-4">
          {{ error || "Die KI-Priorisierung konnte nicht durchgeführt werden. Bitte versuchen Sie es erneut." }}
        </ComponentAlert>
      </template>

      <template v-else-if="state === 'result'">
        <h1 class="mb-3 flex items-center gap-2 text-2xl">
          <ComponentIconAiSparkle class="h-6 w-auto" />
          KI-Priorisierung

          <MedInfoButton
            size="lg"
            side="top"
            aria-label="KI-Priorisierung Info"
            disable-portal
            :data-test="getTestId('ai-prioritization-info-button')"
          >
            <p class="text-sm">
              Ihre Daten sind jederzeit geschützt. Die MediCheck-KI generiert keine neuen Fakten, sondern filtert die
              MediCheck-Ergebnisse nach klinischer Relevanz.
            </p>
            <p class="mt-4 text-sm">
              Bitte beachten Sie, dass jede KI Fehler machen oder unvollständige Angaben erzeugen kann. Überprüfen Sie
              daher alle Fakten und wichtigen Informationen sorgfältig.
            </p>
          </MedInfoButton>

          <MedTag label="BETA" variant="blue" />
        </h1>

        <p class="mb-4">
          Die MediCheck-KI schlägt Ihnen
          <strong>{{ resultsLabel }}</strong>
          vor:
        </p>

        <div class="space-y-6">
          <div v-for="group in groupedItems" :key="group.check.id" class="grid grid-cols-[auto_1fr] gap-x-1">
            <!-- empty checkbox column so the header aligns with the entry column -->
            <div />

            <div class="flex items-center space-x-2 border-b-1 bg-gray-100 p-2">
              <h4 class="font-semibold">{{ group.check.checkname }}</h4>

              <MedInfoButton
                size="lg"
                side="top"
                aria-label="Check Information"
                disable-portal
                :data-test="getTestId('ai-prioritization-check-info-button')"
              >
                <div v-if="group.check?.claims?.[0]?.description" class="max-w-sm text-sm">
                  <p class="mb-1 font-semibold">Check Information:</p>
                  <!-- eslint-disable-next-line vue/no-v-html -- claim descriptions come from the Core service as rendered Blade output (AbstractCheckClaim::buildRedlightMouseOverClaimText with format "html") and contain markup such as <br> and links -->
                  <div v-html="group.check.claims[0].description" />
                </div>
                <p v-else class="max-w-sm text-sm">Keine Check Information hinterlegt.</p>
              </MedInfoButton>
            </div>

            <!-- one row per entry: checkbox left, entry right -->
            <template v-for="(item, idx) in group.items" :key="item.section">
              <div class="flex items-center">
                <MedFormField orientation="horizontal">
                  <MedCheckbox
                    v-model="selected[item.section]"
                    :data-test="getTestId('ai-prioritization-section-checkbox-' + item.section)"
                  />
                  <MedLabel class="sr-only">{{ item.claim.summary }}</MedLabel>
                </MedFormField>
              </div>

              <div class="min-w-0">
                <PagesRecordResultListClaimItem
                  :patient-id="record.patient_id"
                  :record-id="record.id"
                  :mc-check-id="item.check.mc_check_id"
                  :claim="item.claim"
                  :claim-number="idx + 1"
                  :has-sibling-claims="group.items.length > 1"
                  :read-only="true"
                  :ai-prioritized="true"
                  :visible-tabs="['tab-original']"
                />
              </div>
            </template>
          </div>
        </div>

        <div v-if="reasoning" class="mt-4">
          <p class="mb-1">Begründung:</p>

          <p class="text-sm whitespace-pre-line text-gray-700">{{ reasoning }}</p>
        </div>

        <p class="mt-2 mb-4 text-sm text-gray-500">
          KI-generierte Inhalte können Fehler enthalten. Bitte überprüfen Sie die Priorisierung.
        </p>

        <ComponentAiRating
          v-model:rating="rating"
          v-model:reason="ratingReason"
          label="War diese KI-Priorisierung hilfreich für Sie?"
          :up-options="upOptions"
          :down-options="downOptions"
          :disabled="isLoading"
          :required="false"
        />
      </template>
    </template>

    <template #actions>
      <template v-if="state === 'hint'">
        <MedButton
          variant="tertiary"
          :data-test="getTestId('ai-prioritization-dialog-start-button')"
          @click="startFromHint"
        >
          <ComponentIconAiSparkle class="h-4 w-auto" :colored="false" />
          KI-Priorisierung starten
        </MedButton>

        <MedButton :data-test="getTestId('ai-prioritization-dialog-cancel-button')" @click="cancelProgress">
          Abbrechen
        </MedButton>
      </template>

      <template v-else-if="state === 'progress'">
        <MedButton
          variant="primary"
          :is-loading="true"
          :disabled="true"
          :data-test="getTestId('ai-prioritization-dialog-loading-button')"
        >
          KI-Priorisierung läuft
        </MedButton>

        <MedButton :data-test="getTestId('ai-prioritization-dialog-cancel-button')" @click="cancelProgress">
          Abbrechen
        </MedButton>
      </template>

      <template v-else-if="state === 'error'">
        <MedButton variant="tertiary" :data-test="getTestId('ai-prioritization-dialog-retry-button')" @click="retry">
          Erneut versuchen
        </MedButton>

        <MedButton :data-test="getTestId('ai-prioritization-dialog-cancel-button')" @click="cancelProgress">
          Abbrechen
        </MedButton>
      </template>

      <template v-else-if="state === 'result'">
        <MedButton variant="tertiary" :data-test="getTestId('ai-prioritization-dialog-apply-button')" @click="apply">
          <ComponentIconAiSparkle class="h-4 w-auto" :colored="false" />
          Priorisierung anwenden
        </MedButton>

        <MedButton :data-test="getTestId('ai-prioritization-dialog-close-button')" @click="cancelResult()">
          Abbrechen
        </MedButton>
      </template>
    </template>
  </ComponentDialog>
</template>
